import { Timeout } from '../../../types'
import { pubSubGrid } from '../pubSub'

//
// Scroll cursor for the mobile preview-on-scroll grid. The preview index only
// lands on a tile once the scroll settles, so a fast scroll looks like tiles
// lighting up at random. The cursor tracks the same row/column math on every
// scroll event and walks tile by tile (row-major, so left to right then down)
// toward its target, giving the eye a continuous sweep to follow that ends
// on the tile about to show its preview.
//
// Module state, notified per tile — a cursor move only ever re-renders the
// tile it left and the tile it landed on, never the grid.
//

export type GridTileIndex = {
    column: number | null
    row: number | null
}

const emptyTileIndex: GridTileIndex = { column: null, row: null }

// per-tile cadence of the sweep; a three-column row sweeps in ~120ms, well
// inside the preview's settle debounce so the cursor is on the preview tile
// before the preview starts
export const SCROLL_CURSOR_STEP_MS = 40

const scrollCursor = {
    columnCount: 0,
    current: emptyTileIndex,
    stepTimeout: null as Timeout | null,
    target: emptyTileIndex,
}

export const scrollCursorChannel = (row: number, column: number) =>
    `cursor::${row}:${column}`

export const isScrollCursorTile = (row: number, column: number) => {
    const { current } = scrollCursor

    return current.row === row && current.column === column
}

export const getScrollCursorTile = (): GridTileIndex => scrollCursor.current

const isSameTile = (a: GridTileIndex, b: GridTileIndex) =>
    a.row === b.row && a.column === b.column

// row 0 is the banner row, so tile order starts at row 1
const toLinearIndex = ({ row, column }: GridTileIndex, columnCount: number) =>
    (row - 1) * columnCount + column

const fromLinearIndex = (
    linearIndex: number,
    columnCount: number,
): GridTileIndex => ({
    column: linearIndex % columnCount,
    row: Math.floor(linearIndex / columnCount) + 1,
})

const commitScrollCursor = (next: GridTileIndex) => {
    const previous = scrollCursor.current

    if (isSameTile(previous, next)) {
        return
    }

    scrollCursor.current = next

    if (typeof previous.row === 'number') {
        pubSubGrid.emit(
            scrollCursorChannel(previous.row, previous.column),
            false,
        )
    }

    if (typeof next.row === 'number') {
        pubSubGrid.emit(scrollCursorChannel(next.row, next.column), true)
    }
}

const clearStepTimeout = () => {
    if (scrollCursor.stepTimeout === null) {
        return
    }

    clearTimeout(scrollCursor.stepTimeout)
    scrollCursor.stepTimeout = null
}

const stepScrollCursor = () => {
    scrollCursor.stepTimeout = null

    const { columnCount, current, target } = scrollCursor

    if (typeof target.row !== 'number' || typeof current.row !== 'number') {
        commitScrollCursor(target)
        return
    }

    const from = toLinearIndex(current, columnCount)
    const to = toLinearIndex(target, columnCount)

    if (from === to) {
        return
    }

    const direction = to > from ? 1 : -1

    commitScrollCursor(fromLinearIndex(from + direction, columnCount))

    if (from + direction !== to) {
        scrollCursor.stepTimeout = setTimeout(
            stepScrollCursor,
            SCROLL_CURSOR_STEP_MS,
        )
    }
}

export const setScrollCursorTargetState = (
    row: number | null,
    column: number | null,
    columnCount: number,
) => {
    const target: GridTileIndex = { column, row }

    if (
        isSameTile(scrollCursor.target, target) &&
        scrollCursor.columnCount === columnCount
    ) {
        return
    }

    scrollCursor.columnCount = columnCount
    scrollCursor.target = target

    if (typeof row !== 'number' || columnCount < 1) {
        clearStepTimeout()
        commitScrollCursor(emptyTileIndex)
        return
    }

    const { current } = scrollCursor

    if (typeof current.row === 'number') {
        // a fling can move the target several rows at once; rather than
        // queue every tile in between, jump so the sweep covers at most one
        // row's worth of tiles into the target and never trails the scroll
        const from = toLinearIndex(current, columnCount)
        const to = toLinearIndex(target, columnCount)
        const gap = to - from

        if (Math.abs(gap) > columnCount) {
            commitScrollCursor(
                fromLinearIndex(to - Math.sign(gap) * columnCount, columnCount),
            )
        }
    }

    // take the first step now so the cursor moves with the scroll event, and
    // let the chain of timeouts carry the rest
    clearStepTimeout()
    stepScrollCursor()
}

export const resetScrollCursorState = () => {
    setScrollCursorTargetState(null, null, 0)
}
